/**
 * Crew cargo check (no-manifest stops): copy and pure form → request mapping.
 * Server: POST /trips/:tripId/stops/:stopId/cargo-check (docs/CREW_API_CONTRACT.md §14).
 * Android ports these strings and rules.
 */

export type CargoCheckMode = 'piece_count' | 'pic_confirmation';
export type CargoMismatchReason = 'short' | 'extra' | 'damaged' | 'other';

export const CARGO_COPY = {
  title: 'Confirm cargo',
  booked: (n: number) => `Booked: ${n} piece${n === 1 ? '' : 's'}`,
  allHere: (n: number) => `${n} piece${n === 1 ? '' : 's'} — all here`,
  differentCount: 'Different count',
  sameCount: 'Back to booked count',
  countedLabel: 'Pieces counted',
  reasonLabel: 'Why is the count different?',
  noteLabel: 'Note (optional)',
  notePlaceholder: 'What happened?',
  photoLabel: 'Photo (optional)',
  saveCount: 'Save count',
  picNameLabel: 'PIC name (person in charge at the site)',
  picNamePlaceholder: 'Full name of the PIC',
  picConfirmLoaded: 'PIC confirmed everything is loaded',
  picConfirmUnloaded: 'PIC confirmed everything is unloaded',
  confirmPic: 'Confirm cargo',
  mismatchHint: (n: number) => `This is different from the booking (${n}). Dispatch will be told.`,
  errPicName: "Enter the PIC's name.",
  errPicConfirm: 'Tick the box once the PIC has confirmed.',
  errCount: 'Enter the number of pieces counted.',
  errReason: 'Choose why the count is different.',
  savedOk: 'Cargo confirmed',
  savedMismatch: 'Count saved — dispatch has been told about the difference',
  confirmedSummary: 'Cargo confirmed',
} as const;

export const CARGO_REASON_CHIPS: Array<{ reason: CargoMismatchReason; label: string }> = [
  { reason: 'short', label: 'Short' },
  { reason: 'extra', label: 'Extra' },
  { reason: 'damaged', label: 'Damaged' },
  { reason: 'other', label: 'Other' },
];

export type CargoForm = {
  mode: CargoCheckMode;
  expectedPieces: number | null;
  picName: string;
  /** piece_count: "all here" uses the expected count; otherwise the typed value. */
  allHere?: boolean;
  counted?: string;
  reason?: CargoMismatchReason | '';
  note?: string;
  photo?: string;
  picConfirmed?: boolean;
};

export type CargoRequestBody = {
  mode: CargoCheckMode;
  countedPieces?: number;
  picName: string;
  picConfirmed?: boolean;
  mismatchReason?: CargoMismatchReason;
  note?: string;
  photo?: string;
};

/** Validates the crew form and builds the request body, or returns the first error. */
export function buildCargoCheckBody(form: CargoForm): { body: CargoRequestBody } | { error: string } {
  const picName = form.picName.trim();
  if (!picName) return { error: CARGO_COPY.errPicName };
  const note = form.note?.trim() || undefined;
  const photo = form.photo || undefined;

  if (form.mode === 'pic_confirmation') {
    if (!form.picConfirmed) return { error: CARGO_COPY.errPicConfirm };
    return { body: { mode: 'pic_confirmation', picName, picConfirmed: true, note, photo } };
  }

  let counted: number;
  if (form.allHere && form.expectedPieces != null) {
    counted = form.expectedPieces;
  } else {
    const raw = (form.counted ?? '').trim();
    if (!/^\d+$/.test(raw)) return { error: CARGO_COPY.errCount };
    counted = Number(raw);
  }
  const mismatch = form.expectedPieces != null && counted !== form.expectedPieces;
  if (mismatch && !form.reason) return { error: CARGO_COPY.errReason };
  return {
    body: {
      mode: 'piece_count',
      countedPieces: counted,
      picName,
      ...(mismatch ? { mismatchReason: form.reason as CargoMismatchReason } : {}),
      note,
      photo,
    },
  };
}

export function isCountMismatch(expected: number | null, counted: string): boolean {
  if (expected == null) return false;
  const raw = counted.trim();
  return /^\d+$/.test(raw) && Number(raw) !== expected;
}
